/**
 * Extracts question/answer pairs from rendered article HTML so blog posts with
 * real Q&A sections can emit FAQPage JSON-LD.
 *
 * A pair is only produced when an <h2>/<h3>/<h4> heading reads as a question
 * (ends with "?") and is followed by enough prose to be a useful answer.
 */

export type FaqPair = { q: string; a: string };

const HEADING_RE = /<h([2-4])[^>]*>([\s\S]*?)<\/h\1>/gi;
const MIN_ANSWER_CHARS = 60;
const MIN_PAIRS = 2;
const MAX_ANSWER_CHARS = 900;

function toText(html: string) {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function truncate(text: string, limit: number) {
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastStop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("? "), cut.lastIndexOf("! "));
  return (lastStop > limit * 0.5 ? cut.slice(0, lastStop + 1) : cut.trimEnd() + "…").trim();
}

export function extractFaqPairs(html?: string): FaqPair[] {
  if (!html) return [];
  const headings: { level: number; text: string; start: number; end: number }[] = [];
  HEADING_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = HEADING_RE.exec(html))) {
    headings.push({
      level: Number(m[1]),
      text: toText(m[2]),
      start: m.index,
      end: m.index + m[0].length,
    });
  }

  const pairs: FaqPair[] = [];
  const seen = new Set<string>();

  headings.forEach((h, i) => {
    if (!h.text.endsWith("?") || h.text.length < 10) return;
    // Answer runs until the next heading of the same or higher rank.
    const next = headings.slice(i + 1).find((n) => n.level <= h.level);
    const answer = truncate(toText(html.slice(h.end, next ? next.start : html.length)), MAX_ANSWER_CHARS);
    if (answer.length < MIN_ANSWER_CHARS) return;
    const key = h.text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    pairs.push({ q: h.text, a: answer });
  });

  return pairs.length >= MIN_PAIRS ? pairs : [];
}
